import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { select, Store } from '@ngrx/store';

import { isUndefined } from 'lodash-es';
import { Observable, throttleTime } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import * as fromWalkingFarmActions from '../../../../store/actions/walking-farm.actions';
import * as fromWalkingFarmSelectors from '../../../../store/selectors/walking-farm.selectors';
import { OwnerOccupiedFilter, PropertyTypeFilter } from '../../../../typings/walking-farm';

interface FiltersFormGroup {
  filterByBathroomsNumber: FormControl<{ lower: number; upper: number } | null>;
  filterByBedroomsNumber: FormControl<{ lower: number; upper: number } | null>;
  filterByLivingArea: FormControl<{ lower: number; upper: number } | null>;
  filterByNoSalesWithinYears: FormControl<number>;
  filterByOwnerOccupied: FormControl<OwnerOccupiedFilter>;
  filterByPropertyType: FormControl<PropertyTypeFilter[] | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-filter',
  standalone: false,
  templateUrl: './filter.component.html'
})
export class FilterPageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);

  filterForm: FormGroup<FiltersFormGroup> = new FormGroup<FiltersFormGroup>({
    filterByBathroomsNumber: new FormControl<{ lower: number; upper: number }>({ lower: 0, upper: 5 }),
    filterByBedroomsNumber: new FormControl<{ lower: number; upper: number }>({ lower: 0, upper: 5 }),
    filterByLivingArea: new FormControl<{ lower: number; upper: number }>({ lower: 0, upper: 5000 }),
    filterByNoSalesWithinYears: new FormControl<number>(0, {
      nonNullable: true
    }),
    filterByOwnerOccupied: new FormControl<OwnerOccupiedFilter>(null),
    filterByPropertyType: new FormControl<PropertyTypeFilter[]>([])
  });

  propertyTypes$: Observable<PropertyTypeFilter[]>;

  defaultBackHref: string | undefined;
  defaultHref: string;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];
    this.defaultHref = [this.defaultBackHref ?? '', 'walking-farm'].join('/');

    this.propertyTypes$ = this.store.pipe(select(fromWalkingFarmSelectors.selectFilterAvailablePropertyTypes));

    this.filterForm.valueChanges
      .pipe(throttleTime(500, undefined, { leading: true, trailing: true }), distinctUntilChanged())
      .subscribe((values) => {
        this.store.dispatch(
          fromWalkingFarmActions.updateNewFarmFilters({
            payload: {
              filterByMaxBathrooms:
                isUndefined(values.filterByBathroomsNumber?.upper) || values.filterByBathroomsNumber?.upper === 5
                  ? null
                  : values.filterByBathroomsNumber?.upper,
              filterByMaxBedrooms:
                isUndefined(values.filterByBedroomsNumber?.upper) || values.filterByBedroomsNumber?.upper === 5
                  ? null
                  : values.filterByBedroomsNumber?.upper,
              filterByMaxLivingArea:
                isUndefined(values.filterByLivingArea?.upper) || values.filterByLivingArea?.upper === 5000
                  ? null
                  : values.filterByLivingArea?.upper,
              filterByMinBathrooms:
                isUndefined(values.filterByBathroomsNumber?.lower) || values.filterByBathroomsNumber?.lower === 0
                  ? null
                  : values.filterByBathroomsNumber?.lower,
              filterByMinBedrooms:
                isUndefined(values.filterByBedroomsNumber?.lower) || values.filterByBedroomsNumber?.lower === 0
                  ? null
                  : values.filterByBedroomsNumber?.lower,
              filterByMinLivingArea:
                isUndefined(values.filterByLivingArea?.lower) || values.filterByLivingArea?.lower === 0
                  ? null
                  : values.filterByLivingArea?.lower,
              filterByNoSalesWithinYears: values.filterByNoSalesWithinYears ?? 0,
              filterByOwnerOccupied: isUndefined(values.filterByOwnerOccupied) ? null : values.filterByOwnerOccupied,
              filterByPropertyType: values.filterByPropertyType ?? []
            }
          })
        );
      });

    this.store
      .pipe(select(fromWalkingFarmSelectors.selectFilters))
      .pipe(throttleTime(500, undefined, { leading: true, trailing: true }), distinctUntilChanged())
      .subscribe((filters) => {
        this.filterForm.patchValue(
          {
            filterByBathroomsNumber: {
              lower: filters.filterByMinBathrooms ?? 0,
              upper: filters.filterByMaxBathrooms ?? 5
            },
            filterByBedroomsNumber: {
              lower: filters.filterByMinBedrooms ?? 0,
              upper: filters.filterByMaxBedrooms ?? 5
            },
            filterByLivingArea: {
              lower: filters.filterByMinLivingArea ?? 0,
              upper: filters.filterByMaxLivingArea ?? 5000
            },
            filterByNoSalesWithinYears: filters.filterByNoSalesWithinYears,
            filterByOwnerOccupied: filters.filterByOwnerOccupied,
            filterByPropertyType: filters.filterByPropertyType ?? []
          },
          { emitEvent: false }
        );
      });
  }

  clearFilters() {
    this.store.dispatch(fromWalkingFarmActions.clearNewFarmFilters());
  }

  updateFilterByNoSalesWithinYears(increment: boolean) {
    const currentValue: number = this.filterForm.controls.filterByNoSalesWithinYears.getRawValue() ?? 0;
    if (increment && currentValue < 30) {
      this.filterForm.patchValue({
        filterByNoSalesWithinYears: currentValue + 1
      });
    } else if (!increment && currentValue > 0) {
      this.filterForm.patchValue({
        filterByNoSalesWithinYears: currentValue - 1
      });
    }
  }

  comparePropertyTypes(a: PropertyTypeFilter, b: PropertyTypeFilter) {
    return a && b ? a.id === b.id : a === b;
  }
}
